import React, { useState } from 'react';
import { entriesApi } from '@/lib/api';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { Plus, Trash2, ListPlus } from 'lucide-react';
import { CATEGORIES, PAYMENT_MODES, SUB_CAT_CATEGORIES, SUB_CAT_LABEL, getEntityTypeForCategory } from '@/constants/categories';

const ENTRY_TYPES = [
  { value: 'expense', label: 'Out' },
  { value: 'income', label: 'In' },
];

const makeRow = (lastDate) => ({
  date: lastDate || new Date().toISOString().slice(0, 10),
  amount: '',
  notes: '',
  entryType: 'expense',
  paymentMode: 'cash',
});

export function QuickMultiEntryDialog({ open, onOpenChange, sites, entities, subCategories, entityBankAccounts, fetchEntityBankAccounts, onSuccess }) {
  // Common fields (set once)
  const [siteId, setSiteId] = useState('');
  const [category, setCategory] = useState('material');
  const [subCategory, setSubCategory] = useState('');
  const [entityId, setEntityId] = useState('');
  const [entityName, setEntityName] = useState('');
  const [bankAccountId, setBankAccountId] = useState('');
  const [bankAccountName, setBankAccountName] = useState('');
  const [submitting, setSubmitting] = useState(false);

  // Rows - date, amount, notes, entryType, paymentMode per row
  const [rows, setRows] = useState([makeRow()]);

  const filteredSubCats = subCategories.filter(sc => sc.parent_category === category);
  const showSubCat = SUB_CAT_CATEGORIES.includes(category);

  const catEntities = entities.filter(e => {
    const t = getEntityTypeForCategory(category);
    return t ? e.entity_type === t : true;
  });

  const handleEntityChange = (v) => {
    if (v === 'none') {
      setEntityId(''); setEntityName(''); setBankAccountId(''); setBankAccountName('');
      return;
    }
    const ent = entities.find(e => e._id === v);
    setEntityId(v);
    setEntityName(ent?.name || '');
    setBankAccountId('');
    setBankAccountName('');
    if (fetchEntityBankAccounts) fetchEntityBankAccounts(v);
  };

  const handleBankAccountChange = (v) => {
    if (v === 'none') { setBankAccountId(''); setBankAccountName(''); return; }
    const acc = entityBankAccounts.find(a => a._id === v);
    setBankAccountId(v);
    setBankAccountName(acc ? `${acc.account_name}${acc.bank_name ? ' - ' + acc.bank_name : ''}` : '');
  };

  const addRow = () => {
    const lastDate = rows.length > 0 ? rows[rows.length - 1].date : new Date().toISOString().slice(0, 10);
    setRows(prev => [...prev, makeRow(lastDate)]);
  };

  const removeRow = (idx) => {
    if (rows.length <= 1) return;
    setRows(prev => prev.filter((_, i) => i !== idx));
  };

  const updateRow = (idx, field, value) => {
    setRows(prev => prev.map((r, i) => i === idx ? { ...r, [field]: value } : r));
  };

  // Bulk set type or payment mode for all rows
  const setAllRowsField = (field, value) => {
    setRows(prev => prev.map(r => ({ ...r, [field]: value })));
  };

  const handleSubmit = async () => {
    const validRows = rows.filter(r => r.date && r.amount && parseFloat(r.amount) > 0);
    if (validRows.length === 0 || !siteId) return;
    setSubmitting(true);
    try {
      const payload = validRows.map(r => ({
        date: r.date,
        site_id: siteId,
        entry_type: r.entryType,
        category,
        sub_category: subCategory,
        entity_id: entityId,
        entity_name: entityName,
        payment_mode: r.paymentMode,
        bank_account_id: r.paymentMode === 'bank' ? bankAccountId : '',
        bank_account_name: r.paymentMode === 'bank' ? bankAccountName : '',
        amount: r.amount,
        notes: r.notes
      }));
      await entriesApi.bulkUpload(payload);
      onSuccess?.();
      onOpenChange(false);
      setRows([makeRow()]);
    } catch {}
    setSubmitting(false);
  };

  const totalAmount = rows.reduce((s, r) => s + (parseFloat(r.amount) || 0), 0);
  const validCount = rows.filter(r => r.date && r.amount && parseFloat(r.amount) > 0).length;
  const anyBankRow = rows.some(r => r.paymentMode === 'bank');

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2"><ListPlus className="w-5 h-5" /> Quick Multi-Entry</DialogTitle>
          <DialogDescription>Set common fields once. Type & Payment Mode can be changed per row.</DialogDescription>
        </DialogHeader>
        <div className="space-y-4" data-testid="quick-multi-entry-dialog">
          {/* Common Fields */}
          <div className="bg-muted/50 border border-border p-4 space-y-3">
            <div className="text-xs font-bold uppercase tracking-wider text-muted-foreground">Common Fields (apply to all rows)</div>
            <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
              <div>
                <Label className="text-xs text-muted-foreground">Site</Label>
                <Select value={siteId} onValueChange={setSiteId}>
                  <SelectTrigger className="mt-1 h-8 text-xs" data-testid="qme-site"><SelectValue placeholder="Select site" /></SelectTrigger>
                  <SelectContent>{sites.map(s => <SelectItem key={s._id} value={s._id}>{s.name}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-xs text-muted-foreground">Category</Label>
                <Select value={category} onValueChange={v => { setCategory(v); setSubCategory(''); setEntityId(''); setEntityName(''); }}>
                  <SelectTrigger className="mt-1 h-8 text-xs" data-testid="qme-category"><SelectValue /></SelectTrigger>
                  <SelectContent>{CATEGORIES.map(c => <SelectItem key={c.value} value={c.value}>{c.label}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              {showSubCat && (
                <div>
                  <Label className="text-xs text-muted-foreground">{SUB_CAT_LABEL[category] || 'Sub-Type'}</Label>
                  <Select value={subCategory} onValueChange={v => setSubCategory(v === 'none' ? '' : v)}>
                    <SelectTrigger className="mt-1 h-8 text-xs" data-testid="qme-sub-cat"><SelectValue placeholder="Select..." /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="none">-- None --</SelectItem>
                      {filteredSubCats.map(sc => <SelectItem key={sc._id} value={sc.name}>{sc.name}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
              )}
              <div>
                <Label className="text-xs text-muted-foreground">Agency / Vendor</Label>
                <Select value={entityId} onValueChange={handleEntityChange}>
                  <SelectTrigger className="mt-1 h-8 text-xs" data-testid="qme-entity"><SelectValue placeholder="Select..." /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">-- None --</SelectItem>
                    {catEntities.map(e => <SelectItem key={e._id} value={e._id}>{e.name}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
            </div>
            {anyBankRow && entityId && entityBankAccounts.length > 0 && (
              <div>
                <Label className="text-xs text-muted-foreground">Bank Account (for rows with Bank payment)</Label>
                <Select value={bankAccountId} onValueChange={handleBankAccountChange}>
                  <SelectTrigger className="mt-1 h-8 text-xs" data-testid="qme-bank-account"><SelectValue placeholder="Select bank account" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">-- No specific account --</SelectItem>
                    {entityBankAccounts.map(a => (
                      <SelectItem key={a._id} value={a._id}>{a.account_name}{a.bank_name ? ` (${a.bank_name})` : ''}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            )}
          </div>

          {/* Bulk set helpers */}
          <div className="flex items-center gap-3 text-xs text-muted-foreground">
            <span>Set all rows:</span>
            <Button type="button" variant="ghost" size="sm" className="h-6 text-xs px-2" onClick={() => setAllRowsField('entryType', 'expense')} data-testid="qme-set-all-expense">All Out</Button>
            <Button type="button" variant="ghost" size="sm" className="h-6 text-xs px-2" onClick={() => setAllRowsField('entryType', 'income')} data-testid="qme-set-all-income">All In</Button>
            <span className="text-border">|</span>
            <Button type="button" variant="ghost" size="sm" className="h-6 text-xs px-2" onClick={() => setAllRowsField('paymentMode', 'cash')} data-testid="qme-set-all-cash">All Cash</Button>
            <Button type="button" variant="ghost" size="sm" className="h-6 text-xs px-2" onClick={() => setAllRowsField('paymentMode', 'bank')} data-testid="qme-set-all-bank">All Bank</Button>
          </div>

          {/* Rows */}
          <div>
            <div className="flex items-center justify-between mb-2">
              <div className="text-xs font-bold uppercase tracking-wider text-muted-foreground">Entries ({rows.length} rows)</div>
              <Button type="button" variant="outline" size="sm" className="gap-1 h-7 text-xs" onClick={addRow} data-testid="qme-add-row">
                <Plus className="w-3 h-3" /> Add Row
              </Button>
            </div>
            <div className="border border-border overflow-x-auto">
              <table className="w-full text-xs">
                <thead>
                  <tr className="bg-muted border-b border-border">
                    <th className="py-2 px-2 text-left font-semibold text-muted-foreground w-8">#</th>
                    <th className="py-2 px-2 text-left font-semibold text-muted-foreground w-32">Date</th>
                    <th className="py-2 px-2 text-left font-semibold text-muted-foreground w-20">Type</th>
                    <th className="py-2 px-2 text-left font-semibold text-muted-foreground w-20">Cashflow</th>
                    <th className="py-2 px-2 text-left font-semibold text-muted-foreground w-28">Amount ({'\u20B9'})</th>
                    <th className="py-2 px-2 text-left font-semibold text-muted-foreground">Notes / Particulars</th>
                    <th className="py-2 px-2 w-8"></th>
                  </tr>
                </thead>
                <tbody>
                  {rows.map((row, idx) => (
                    <tr key={idx} className="border-b border-border" data-testid={`qme-row-${idx}`}>
                      <td className="py-1.5 px-2 text-muted-foreground">{idx + 1}</td>
                      <td className="py-1.5 px-2">
                        <Input type="date" value={row.date} onChange={e => updateRow(idx, 'date', e.target.value)} className="h-7 text-xs" data-testid={`qme-date-${idx}`} />
                      </td>
                      <td className="py-1.5 px-2">
                        <Select value={row.entryType} onValueChange={v => updateRow(idx, 'entryType', v)}>
                          <SelectTrigger className="h-7 text-xs w-full" data-testid={`qme-type-${idx}`}><SelectValue /></SelectTrigger>
                          <SelectContent>
                            {ENTRY_TYPES.map(t => <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>)}
                          </SelectContent>
                        </Select>
                      </td>
                      <td className="py-1.5 px-2">
                        <Select value={row.paymentMode} onValueChange={v => updateRow(idx, 'paymentMode', v)}>
                          <SelectTrigger className="h-7 text-xs w-full" data-testid={`qme-payment-${idx}`}><SelectValue /></SelectTrigger>
                          <SelectContent>
                            {PAYMENT_MODES.map(m => <SelectItem key={m.value} value={m.value}>{m.label}</SelectItem>)}
                          </SelectContent>
                        </Select>
                      </td>
                      <td className="py-1.5 px-2">
                        <Input type="number" step="0.01" min="0" value={row.amount} onChange={e => updateRow(idx, 'amount', e.target.value)} className="h-7 text-xs font-mono" placeholder="0.00" data-testid={`qme-amount-${idx}`} />
                      </td>
                      <td className="py-1.5 px-2">
                        <Input value={row.notes} onChange={e => updateRow(idx, 'notes', e.target.value)} className="h-7 text-xs" placeholder="Description..." data-testid={`qme-notes-${idx}`} />
                      </td>
                      <td className="py-1.5 px-2">
                        {rows.length > 1 && (
                          <button onClick={() => removeRow(idx)} className="p-1 hover:bg-muted rounded text-red-600" data-testid={`qme-remove-${idx}`}>
                            <Trash2 className="w-3 h-3" />
                          </button>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>

          {/* Summary + Submit */}
          <div className="flex items-center justify-between pt-2">
            <div className="text-sm text-muted-foreground">
              <span className="font-mono font-bold text-foreground">{validCount}</span> valid entries | Total: <span className="font-mono font-bold text-foreground">{'\u20B9'}{totalAmount.toLocaleString('en-IN')}</span>
            </div>
            <Button onClick={handleSubmit} disabled={submitting || validCount === 0 || !siteId} data-testid="qme-submit">
              {submitting ? 'Creating...' : `Create ${validCount} Entries`}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
